// src/pages/Roles.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockResetIcon from "@mui/icons-material/LockReset";
import TocIcon from "@mui/icons-material/Toc";
import { ref, onValue, get, set, update } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import { db, app, functions as fbFunctions } from "../firebase";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";

/* -------- helpers (similar to Employees.jsx) -------- */
function generatePassword(len = 12) {
  const alphabet =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*()_+-=";
  const array = new Uint32Array(len);
  if (
    typeof window !== "undefined" &&
    window.crypto &&
    window.crypto.getRandomValues
  ) {
    window.crypto.getRandomValues(array);
  } else {
    // fallback
    for (let i = 0; i < len; i++)
      array[i] = Math.floor(Math.random() * alphabet.length);
  }
  return Array.from(array, (x) => alphabet[x % alphabet.length]).join("");
}

async function createAuthUser(email, password) {
  const apiKey = app?.options?.apiKey;
  if (!apiKey) throw new Error("Missing Firebase apiKey");
  const res = await fetch(
    `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${apiKey}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, returnSecureToken: false }),
    }
  );
  const text = await res.text().catch(() => "");
  if (!res.ok) {
    try {
      const j = JSON.parse(text || "{}");
      throw new Error(j.error?.message || text || "Failed to create auth user");
    } catch (e) {
      throw new Error(text || "Failed to create auth user");
    }
  }
  const data = JSON.parse(text || "{}");
  return data.localId;
}

/* -------- role names & helpers -------- */
const ROLE_MAIN = "MAIN_ADMIN";
const ROLE_ADMIN = "ADMIN";
const ROLE_HR = "HR";
const ALL_ROLES = [ROLE_MAIN, ROLE_ADMIN, ROLE_HR];

function displayRoleLabel(role) {
  const r = String(role || "").toUpperCase();
  if (r === ROLE_MAIN) return "S-ADMIN";
  if (r === ROLE_ADMIN) return "Admin";
  if (r === ROLE_HR) return "HR";
  return role || "—";
}

/* -------- Page component -------- */
export default function RolesPage() {
  const nav = useNavigate();
  const [adminsMap, setAdminsMap] = useState({});
  const [usersMap, setUsersMap] = useState({});
  const [totpMap, setTotpMap] = useState({});
  const [loading, setLoading] = useState(true);

  // create dialog state
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [cName, setCName] = useState("");
  const [cEmail, setCEmail] = useState("");
  const [cRole, setCRole] = useState(ROLE_HR);
  const [cPassword, setCPassword] = useState("");

  // edit dialog state (role changes)
  const [openEdit, setOpenEdit] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // { uid, email, role, fullName }
  const [editRole, setEditRole] = useState("");

  // password modal state (separate)
  const [openPwd, setOpenPwd] = useState(false);
  const [pwdTarget, setPwdTarget] = useState(null); // user object
  const [pwdValue, setPwdValue] = useState("");
  const [pwdGenerated, setPwdGenerated] = useState("");

  // confirmation dialogs
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type: "delete"|"totp", uid, payload }
  const [msg, setMsg] = useState("");

  // current user + role (read from /admins)
  const [currentUid, setCurrentUid] = useState(null);
  const [currentRole, setCurrentRole] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // load admins, users and totp secrets
  useEffect(() => {
    const aRef = ref(db, "/admins");
    const uRef = ref(db, "/users");
    const tRef = ref(db, "/adminTotpSecrets");

    const unsubAdmins = onValue(aRef, (snap) => {
      setAdminsMap(snap.val() || {});
      setLoading(false);
    });

    const unsubUsers = onValue(uRef, (snap) => {
      setUsersMap(snap.val() || {});
    });

    const unsubTotp = onValue(tRef, (snap) => {
      setTotpMap(snap.val() || {});
    });

    // current auth user
    const unsubAuth = auth.onAuthStateChanged(async (u) => {
      if (!u) {
        setCurrentUid(null);
        setCurrentRole(null);
        setAuthLoading(false);
        nav("/login", { replace: true });
        return;
      }
      setCurrentUid(u.uid);
      try {
        const rsnap = await get(ref(db, `/admins/${u.uid}`));
        setCurrentRole((rsnap.val() && rsnap.val().role) || null);
      } catch (e) {
        setCurrentRole(null);
      } finally {
        setAuthLoading(false);
      }
    });

    return () => {
      unsubAdmins();
      unsubUsers();
      unsubTotp();
      unsubAuth();
    };
  }, [nav]);

  // Only MAIN_ADMIN and ADMIN can view this page
  useEffect(() => {
    if (authLoading) return;
    const norm = String(currentRole || "").toUpperCase();
    if (![ROLE_MAIN, ROLE_ADMIN].includes(norm)) {
      nav("/dashboard", { replace: true, state: { error: "Unauthorized" } });
    }
  }, [authLoading, currentRole, nav]);

  // derived rows
  const rows = useMemo(() => {
    return Object.entries(adminsMap)
      .map(([uid, r]) => {
        const user = usersMap[uid] || {};
        const totpRec = totpMap[uid] || null;
        return {
          uid,
          role: String((r && r.role) || "").toUpperCase(),
          active: r?.active !== false,
          email: user.email || r?.email || "—",
          fullName: user.fullName || r?.fullName || (user.displayName || "—"),
          totpEnabled: !!(totpRec && totpRec.enabled === true),
          totpPresent: !!totpRec,
        };
      })
      .sort((a, b) => ALL_ROLES.indexOf(a.role) - ALL_ROLES.indexOf(b.role));
  }, [adminsMap, usersMap, totpMap]);

  const existingMainAdminUid = useMemo(() => {
    for (const [uid, r] of Object.entries(adminsMap || {})) {
      if (String(r.role || "").toUpperCase() === ROLE_MAIN) return uid;
    }
    return null;
  }, [adminsMap]);

  /* -------- create handler -------- */
  async function handleCreateSubmit(e) {
    e?.preventDefault();
    setMsg("");

    // validations
    if (!cName?.trim()) return setMsg("Full name required");
    if (!cEmail?.trim()) return setMsg("Email required");
    if (!cPassword || cPassword.length < 6) return setMsg("Password (min 6 chars) is required");

    const roleWanted = String(cRole || "").toUpperCase();
    const myRole = String(currentRole || "").toUpperCase();

    // permission checks
    if (![ROLE_MAIN, ROLE_ADMIN].includes(myRole)) return setMsg("Unauthorized");

    // UI does not show MAIN_ADMIN option; double-check defensively
    if (roleWanted === ROLE_MAIN) {
      return setMsg("Cannot create S-ADMIN from UI.");
    }

    // ADMIN cannot create ADMIN
    if (myRole === ROLE_ADMIN && roleWanted === ROLE_ADMIN) {
      return setMsg("ADMIN cannot create ADMIN. ADMIN can create only HR.");
    }

    setCreating(true);
    try {
      // create auth user with provided password
      const pwd = cPassword.trim();
      const uid = await createAuthUser(cEmail.trim().toLowerCase(), pwd);

      const now = Date.now();

      // write only admins/{uid} as you requested
      await set(ref(db, `/admins/${uid}`), {
        role: roleWanted,
        active: true,
        createdAt: now,
        createdBy: currentUid,
        email: cEmail.trim().toLowerCase(),
        fullName: cName.trim(),
      });

      // best-effort: send email via callable if functions configured (no password in mail)
      try {
        const call = httpsCallable(fbFunctions, "sendMailCallable");
        const subject = "You have been granted admin access — ThinkersClub";
        const roleLabel = displayRoleLabel(roleWanted);
        const html = `<p>Hi ${cName.trim()},</p>
          <p>You have been granted <b>${roleLabel}</b> access to the ThinkersClub admin panel.</p>
          <p>Login at <a href="https://admin.thinkersclub.online" target="_blank">admin.thinkersclub.online</a> using your email and password.</p>
          <p>If you forget your password, ask an admin to reset it for you.</p>
          <p>— ThinkersClub System</p>`;
        // request sending from System <system@thinkersclub.online>
        await call({ to: cEmail.trim(), subject, html, from: "System <system@thinkersclub.online>" });
      } catch (mailErr) {
        console.warn("sendMailCallable failed", mailErr);
      }

      setMsg("✅ Created successfully.");
      setOpenCreate(false);
      setCEmail("");
      setCName("");
      setCPassword("");
    } catch (err) {
      console.error("Create role error:", err);
      setMsg(String(err?.message || err) || "Failed to create");
    } finally {
      setCreating(false);
    }
  }

  /* -------- open edit dialog -------- */
  function openEditDialog(row) {
    setEditingUser(row);
    setEditRole(row.role);
    setOpenEdit(true);
    setMsg("");
  }

  /* -------- save edit (role change) -------- */
  async function handleSaveEdit() {
    if (!editingUser) return;
    setMsg("");
    const myRole = String(currentRole || "").toUpperCase();
    const targetUid = editingUser.uid;
    const targetIsMain = existingMainAdminUid === targetUid;

    // protect MAIN_ADMIN
    if (targetIsMain) {
      setMsg("MAIN_ADMIN cannot be modified.");
      return;
    }

    // role change validation
    const targetRole = String(editRole || "").toUpperCase();
    if (targetRole === ROLE_MAIN) {
      setMsg("Cannot assign S-ADMIN from UI.");
      return;
    }
    if (myRole === ROLE_ADMIN && targetRole === ROLE_ADMIN) {
      setMsg("ADMIN cannot assign ADMIN role.");
      return;
    }

    try {
      await update(ref(db, `/admins/${targetUid}`), { role: targetRole, updatedAt: Date.now(), updatedBy: currentUid });
      setMsg("✅ Role updated.");
    } catch (e) {
      console.error("save edit error:", e);
      setMsg("Failed to update role.");
    } finally {
      setOpenEdit(false);
    }
  }

  /* -------- password modal helpers -------- */
  function openPasswordModal(row) {
    setPwdTarget(row);
    setPwdValue("");
    setPwdGenerated("");
    setOpenPwd(true);
    setMsg("");
  }
  function handleGeneratePwdForModal() {
    const p = generatePassword(12);
    setPwdGenerated(p);
    setPwdValue("");
  }
  async function handleSetPasswordConfirm() {
    if (!pwdTarget) return setMsg("No target selected");
    const myRole = String(currentRole || "").toUpperCase();
    if (![ROLE_MAIN, ROLE_ADMIN].includes(myRole)) return setMsg("Unauthorized");

    const targetUid = pwdTarget.uid;
    const targetIsMain = existingMainAdminUid === targetUid;
    if (targetIsMain && myRole !== ROLE_MAIN) return setMsg("Only MAIN_ADMIN may change MAIN_ADMIN password");

    const pwdToSet = (pwdValue && pwdValue.trim()) || pwdGenerated;
    if (!pwdToSet || pwdToSet.length < 6) return setMsg("Password must be at least 6 characters.");

    try {
      const call = httpsCallable(fbFunctions, "setUserPassword");
      const res = await call({ uid: targetUid, password: pwdToSet });
      const ok = res && (res.data?.ok || res.ok) || false;
      const err = res && (res.data?.error || res.error) || null;
      if (!ok) throw new Error(err || "Failed to set password");
      setMsg(`✅ Password updated${pwdGenerated ? " (generated shown once)" : ""}.`);
      setOpenPwd(false);
    } catch (e) {
      console.error("set password error:", e);
      setMsg(String(e?.message || e) || "Failed to set password.");
    }
  }

  /* -------- handle TOTP actions (open confirmation) -------- */
  function openTotpConfirm(row, action) {
    // action: "enable" | "disable" | "remove"
    setConfirmAction({ type: "totp", uid: row.uid, action, row });
    setConfirmOpen(true);
  }

  async function performTotpAction() {
    if (!confirmAction || confirmAction.type !== "totp") return;
    setMsg("");
    const { uid, action } = confirmAction;
    try {
      const call = httpsCallable(fbFunctions, "setAdminTotp");
      const res = await call({ uid, action });
      const ok = res && (res.data?.ok || res.ok) || false;
      const err = res && (res.data?.error || res.error) || null;
      if (!ok) throw new Error(err || "Failed to update TOTP");
      setMsg("✅ Authenticator updated.");
    } catch (e) {
      console.error("TOTP error:", e);
      setMsg(String(e?.message || e) || "Failed to update authenticator.");
    } finally {
      setConfirmOpen(false);
    }
  }

  /* -------- delete admin (confirmation dialog) -------- */
  function openDeleteConfirm(row) {
    setConfirmAction({ type: "delete", uid: row.uid, row });
    setConfirmOpen(true);
  }

  async function performDelete() {
    if (!confirmAction || confirmAction.type !== "delete") return;
    setMsg("");
    const uid = confirmAction.uid;
    try {
      const callDelete = httpsCallable(fbFunctions, "deleteAuthUser");
      const res = await callDelete({ uid });
      const okRes = res && (res.data?.ok || res.ok) || false;
      const err = res && (res.data?.error || res.error) || null;
      if (!okRes) throw new Error(err || "Failed to delete user");
      setMsg("✅ Deleted admin and removed from Firebase Auth.");
    } catch (e) {
      console.error("delete error:", e);
      setMsg(String(e?.message || e) || "Failed to delete user.");
    } finally {
      setConfirmOpen(false);
    }
  }

  /* -------- toggle active -------- */
  async function handleToggleActive(uid, makeActive) {
    setMsg("");
    const myRole = String(currentRole || "").toUpperCase();
    if (! [ROLE_MAIN, ROLE_ADMIN].includes(myRole)) return setMsg("Unauthorized");

    // Prevent toggling MAIN_ADMIN
    if (existingMainAdminUid === uid) return setMsg("Cannot change MAIN_ADMIN active status.");

    try {
      await update(ref(db, `/admins/${uid}`), { active: !!makeActive, updatedAt: Date.now(), updatedBy: currentUid });
      setMsg(makeActive ? "✅ Re-enabled." : "✅ Disabled.");
    } catch (e) {
      console.error(e);
      setMsg("Failed to update active flag.");
    }
  }

  /* -------- UI -------- */
  if (authLoading || loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Hide create button for HR role
  const canCreate = currentRole === ROLE_MAIN || currentRole === ROLE_ADMIN;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Roles & Admins
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography>Manage MAIN_ADMIN (Developer), ADMIN and HR accounts.</Typography>
          <Stack direction="row" spacing={1}>
            {canCreate && (
              <Button
                variant="contained"
                onClick={() => {
                  setCRole(currentRole === ROLE_MAIN ? ROLE_ADMIN : ROLE_HR);
                  setOpenCreate(true);
                  setMsg("");
                }}
              >
                + Create account
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {msg && <Alert sx={{ mb: 2 }}>{msg}</Alert>}

      <Card>
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Full Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Authenticator</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const isMain = r.uid === existingMainAdminUid;
                return (
                  <TableRow key={r.uid}>
                    <TableCell>{r.fullName || "—"}</TableCell>
                    <TableCell>{r.email}</TableCell>
                    <TableCell>{displayRoleLabel(r.role)}</TableCell>
                    <TableCell>
                      <Chip label={r.active ? "Active" : "Disabled"} color={r.active ? "success" : "default"} size="small" />
                    </TableCell>

                    <TableCell>
                      {r.totpEnabled ? (
                        <Chip label="TOTP enabled" color="success" size="small" />
                      ) : r.totpPresent ? (
                        <Chip label="TOTP present" color="warning" size="small" />
                      ) : (
                        <Chip label="No TOTP" size="small" />
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {/* Edit button (open edit dialog) - disabled for MAIN_ADMIN */}
                        <IconButton size="small" onClick={() => openEditDialog(r)} disabled={isMain || ![ROLE_MAIN, ROLE_ADMIN].includes(currentRole)}>
                          <EditIcon />
                        </IconButton>

                        {/* Change password button */}
                        <IconButton size="small" onClick={() => openPasswordModal(r)} disabled={isMain || ![ROLE_MAIN, ROLE_ADMIN].includes(currentRole)}>
                          <LockResetIcon />
                        </IconButton>

                        {/* TOTP actions */}
                        {r.totpPresent ? (
                          <>
                            <Button
                              size="small"
                              onClick={() => openTotpConfirm(r, r.totpEnabled ? "disable" : "enable")}
                              disabled={isMain || ![ROLE_MAIN, ROLE_ADMIN].includes(currentRole)}
                            >
                              {r.totpEnabled ? "Disable TOTP" : "Enable TOTP"}
                            </Button>
                            <Button
                              size="small"
                              onClick={() => openTotpConfirm(r, "remove")}
                              disabled={isMain || ![ROLE_MAIN, ROLE_ADMIN].includes(currentRole)}
                            >
                              Reset TOTP
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="small"
                            onClick={() => openTotpConfirm(r, "disable")}
                            disabled={isMain || ![ROLE_MAIN, ROLE_ADMIN].includes(currentRole)}
                          >
                            No TOTP
                          </Button>
                        )}

                        {/* Disable/Enable - disallowed for MAIN_ADMIN */}
                        { r.active ? (
                          <Button size="small" onClick={() => handleToggleActive(r.uid, false)} disabled={isMain || ![ROLE_MAIN, ROLE_ADMIN].includes(currentRole)}>
                            Disable
                          </Button>
                        ) : (
                          <Button size="small" onClick={() => handleToggleActive(r.uid, true)} disabled={isMain || ![ROLE_MAIN, ROLE_ADMIN].includes(currentRole)}>
                            Enable
                          </Button>
                        )}

                        {/* Delete - only MAIN_ADMIN can perform server-side deletion */}
                        <IconButton size="small" color="error" onClick={() => openDeleteConfirm(r)} disabled={isMain || currentRole !== ROLE_MAIN}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create account</DialogTitle>
        <form onSubmit={handleCreateSubmit}>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField label="Full name" value={cName} onChange={(e) => setCName(e.target.value)} required />
              <TextField label="Email" value={cEmail} onChange={(e) => setCEmail(e.target.value)} type="email" required />
              <TextField
                label="Password (min 6 chars)"
                value={cPassword}
                onChange={(e) => setCPassword(e.target.value)}
                type="password"
                required
              />
              <div>
                <Typography variant="caption" color="text.secondary">Role</Typography>
                <Select
                  value={cRole}
                  onChange={(e) => setCRole(e.target.value)}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  {/* MAIN_ADMIN not offered via UI */}
                  {currentRole === ROLE_MAIN && <MenuItem value={ROLE_ADMIN}>ADMIN</MenuItem>}
                  <MenuItem value={ROLE_HR}>HR</MenuItem>
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  Note: S-ADMIN cannot be created from UI and only exists once. ADMIN can create HR only.
                </Typography>
              </div>
            </Stack>
            <Box sx={{ mt: 1 }} />
            {creating && <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}><CircularProgress size={18} /> Creating…</Box>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={creating}>
              Create
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit admin</DialogTitle>
        <DialogContent dividers>
          {editingUser && (
            <Stack spacing={2}>
              <TextField label="Full name" value={editingUser.fullName || ""} disabled />
              <TextField label="Email" value={editingUser.email || ""} disabled />
              <div>
                <Typography variant="caption" color="text.secondary">Role</Typography>
                <Select value={editRole} onChange={(e) => setEditRole(e.target.value)} fullWidth sx={{ mt: 1 }}>
                  {/* UI does not allow assigning S-ADMIN */}
                  {(currentRole === ROLE_MAIN) && <MenuItem value={ROLE_ADMIN}>ADMIN</MenuItem>}
                  <MenuItem value={ROLE_HR}>HR</MenuItem>
                </Select>
              </div>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Password modal */}
      <Dialog open={openPwd} onClose={() => setOpenPwd(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set password</DialogTitle>
        <DialogContent dividers>
          {pwdTarget && (
            <Stack spacing={2}>
              <TextField label="Target" value={`${pwdTarget.fullName || "—"} <${pwdTarget.email || "—"}>`} disabled />
              <Typography variant="caption" color="text.secondary">Set a password (min 6 chars) or generate one. Generated password will be shown once.</Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={handleGeneratePwdForModal}>Generate</Button>
                <Button variant="outlined" onClick={() => { setPwdGenerated(""); setPwdValue(""); }}>Manual</Button>
              </Stack>

              {pwdGenerated ? (
                <Alert severity="info">Generated password: <strong>{pwdGenerated}</strong></Alert>
              ) : (
                <TextField label="New password" value={pwdValue} onChange={(e) => setPwdValue(e.target.value)} type="password" fullWidth />
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenPwd(false)}>Cancel</Button>
          <Button onClick={handleSetPasswordConfirm} variant="contained">Set password</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog (used for delete and TOTP operations) */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirmAction && confirmAction.type === "delete" ? "Confirm Deletion" : "Confirm Action"}
        </DialogTitle>
        <DialogContent dividers>
          {confirmAction && confirmAction.type === "delete" && (
            <Typography>Are you sure you want to permanently delete <b>{confirmAction.row.fullName || confirmAction.row.email}</b>? This will remove their Auth account and DB entries.</Typography>
          )}
          {confirmAction && confirmAction.type === "totp" && (
            <Typography>Are you sure you want to <b>{confirmAction.action}</b> authenticator for <b>{confirmAction.row.fullName || confirmAction.row.email}</b>?</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (!confirmAction) { setConfirmOpen(false); return; }
              if (confirmAction.type === "delete") performDelete();
              if (confirmAction.type === "totp") performTotpAction();
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
